import {afterNextRender, inject, Injectable, signal} from '@angular/core';
import {SwUpdate, VersionReadyEvent} from '@angular/service-worker';
import {filter, interval} from 'rxjs';

@Injectable({providedIn: 'root'})
export class AppUpdateService {
  private readonly swUpdate = inject(SwUpdate);
  countdown = signal(0);
  private intervalId: ReturnType<typeof setInterval> | undefined;
  private isPaused = false;
  private updateInProgress = false;

  init(): void {
    afterNextRender(() => {
      if (!this.swUpdate.isEnabled) return;

      // Debe escucharse antes del primer check para no perder VERSION_READY.
      this.swUpdate.versionUpdates
        .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
        .subscribe(() => {
          if (this.updateInProgress) return;

          this.updateInProgress = true;
          console.log('Nueva versión lista. Recargando...');

          // Angular recomienda recargar: activateUpdate() sin recarga puede
          // mezclar el shell actual con chunks pertenecientes a otra versión.
          document.location.reload();
        });

      void this.checkForUpdate();

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          void this.checkForUpdate();
        }
      });

      window.addEventListener('focus', () => void this.checkForUpdate());
      window.addEventListener('online', () => void this.checkForUpdate());

      // Safari puede restaurar una página desde el back-forward cache sin
      // volver a ejecutar la inicialización de Angular.
      window.addEventListener('pageshow', () => void this.checkForUpdate());

      // Para sesiones que permanecen abiertas durante horas.
      interval(5 * 60_000).subscribe(() => void this.checkForUpdate());
    });
  }

  startCountdown(): void {
    this.countdown.set(60);
    this.isPaused = false;
    this.runInterval();
  }

  private runInterval(): void {
    if (this.intervalId) clearInterval(this.intervalId);

    this.intervalId = setInterval(() => {
      if (this.isPaused) return;

      const value = this.countdown() - 1;
      this.countdown.set(value);
      if (value <= 0) this.updateApp();
    }, 1000);
  }

  updateApp(): void {
    document.location.reload();
  }

  pause(): void {
    this.isPaused = true;
  }

  resume(): void {
    this.isPaused = false;
  }

  private async checkForUpdate(): Promise<void> {
    if (this.updateInProgress) return;

    try {
      // console.log('Buscando nueva versión.');
      await this.swUpdate.checkForUpdate();
    } catch (error) {
      console.error('Error buscando actualización de la app', error);
    }
  }
}
